<template>
  <div v-test="'waiting-list-pro-section'">
    <BaseModal
      v-if="errorMessage"
      :message="$t(`errors.${errorMessage}`)"
      @close="closeErrorModal"
      v-test="'form-error'"
    />
    <BaseRadio
      :options="waitingListOptions"
      v-model="waitingListForSpecificDate"
      v-test="'waiting-list-pro-radio'"
    />
    <div
      v-if="!waitingListForSpecificDate"
      v-test="'waiting-list-options'"
      :class="$style.inner"
    >
      <BaseButtonList
        :class="$style.daysList"
        :label="`${$t('waiting_list.days_of_the_week')}*`"
        v-model="daysOfWeek"
        :options="dayOptions"
        :error="submitted && daysAreInvalid"
        v-test="'days-of-week'"
      />
      <BaseButtonList
        :label="`${$t('waiting_list.parts_of_the_day')}*`"
        v-model="partsOfDay"
        :options="momentOptions"
        :error="submitted && partsAreInvalid"
        v-test="'parts-of-day'"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseButtonList from '@/components/BaseButtonList.vue';
import BaseModal from '@/components/BaseModal.vue';
import BaseRadio from '@/components/BaseRadio.vue';

import { useBookingStore } from '@/stores/booking';
import type { DaysOfWeek, WaitingListAvailabilityPerDay } from '@/types';
import dayjs from 'dayjs';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { loadWaitingListAvailabilities } from './load-waiting-list-per-day';
const { t, d } = useI18n();

defineProps<{
  submitted?: boolean;
  partsAreInvalid?: boolean;
  daysAreInvalid?: boolean;
}>();

const emit = defineEmits(['select', 'back']);

const dayOptions = computed(() => {
  const days: DaysOfWeek[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
  return days.map((day, index) => {
    return {
      label: t(`waiting_list.days.${day.toLowerCase()}`),
      value: day,
      dayNr: index + 1,
      disabled: !checkIsDayAvailable(day),
      tooltip:
        !checkIsDayAvailable(day) &&
        t('waiting_list.service_not_available_this_day')
    };
  });
});

const momentOptions = [
  {
    label: t('waiting_list.parts.morning'),
    value: 'morning'
  },
  {
    label: t('waiting_list.parts.afternoon'),
    value: 'afternoon'
  },
  {
    label: t('waiting_list.parts.evening'),
    value: 'evening'
  }
];

const waitingListForSpecificDate = ref(true);
const { date, daysOfWeek, partsOfDay } = storeToRefs(useBookingStore());

watch(waitingListForSpecificDate, (curValue) => {
  partsOfDay.value = curValue ? [] : ['morning', 'afternoon', 'evening'];
  daysOfWeek.value = curValue
    ? []
    : [
        dayOptions.value.find((item) => item.dayNr === dayjs(date.value).day())
          ?.value || 'MO'
      ];

  emit('select', curValue);
});

watch(date, () => {
  partsOfDay.value = waitingListForSpecificDate.value
    ? []
    : ['morning', 'afternoon', 'evening'];
  daysOfWeek.value = waitingListForSpecificDate.value
    ? []
    : [
        dayOptions.value.find((item) => item.dayNr === dayjs(date.value).day())
          ?.value || 'MO'
      ];
});

const waitingListOptions = computed(() => {
  return [
    {
      label: t('waiting_list.notify_specific', {
        date: date.value ? d(new Date(date.value), 'full') : ''
      }),
      value: true
    },
    {
      label: t('waiting_list.notify_chosen'),
      value: false
    }
  ];
});

const waitingListPerDay = ref<WaitingListAvailabilityPerDay[] | null>(null);
const errorMessage = ref('');
const fetchWaitingListPerDay = () => {
  const { locationId, serviceIds } = useBookingStore();

  if (!locationId) {
    return new Promise((_resolve, reject) =>
      reject(new Error('Location ID is not defined.'))
    );
  }

  return loadWaitingListAvailabilities({
    locationId: locationId,
    serviceIds: serviceIds
  }).then((data) => {
    const hasAvailabilityOffer = !!data?.find(
      (dayOfTheWeek) => dayOfTheWeek.parts.length > 0
    );
    // when there is more than one service selected and availability is not possible return to prev step
    if (serviceIds.length > 1 && !hasAvailabilityOffer) {
      errorMessage.value = 'multi_service_no_offer';
    } else {
      waitingListPerDay.value = data || [];
    }
  });
};

fetchWaitingListPerDay();

const closeErrorModal = () => {
  errorMessage.value = '';
  emit('back');
};

const checkIsDayAvailable = (day: DaysOfWeek): boolean => {
  if (!waitingListPerDay.value) {
    return false;
  }
  const partsForDay = waitingListPerDay.value.find((item) => item.day === day);
  return !!partsForDay && partsForDay.parts.length > 0;
};
</script>

<style lang="scss" module>
.base {
  padding: $spacing $spacing 0;
  border-top: 1px solid $color-border;
  margin-bottom: $spacing;
}

.inner {
  margin-top: $spacing * 2;
}

.button {
  margin-top: $spacing * 2;
  width: 100%;
}

.daysList {
  margin-bottom: $spacing * 2;
}

.align {
  text-align: center;
}
</style>
